/**
 * academyApi — 학생 v2(/api/v2/academy)의 서버 호출.
 *
 * 계약: docs/student-admin-v2/FE-20261009-student-admin-v2-backend-design.html §7
 *       docs/student-admin-v2/backend/BE-20261016-stage6b-calendar-stats-admissions.md
 *
 * 응답은 단계별 백엔드 회신을 따른다. 화면이 쓰는 모양으로 바꾸는 일(norm*)은 이 파일 한 곳에 모은다.
 *
 * 공용 요청 함수(fetchWithTokenRefresh)를 쓰지 않는 이유는 강의 v2(syllabusApi)와 같다: 그쪽은 400·403 을
 * 고정 문구로 갈아 끼우고 상태 코드와 서버의 사유(data.reason)를 버린다. 학생 v2 는 사유마다 화면이 다르게 말한다
 * (설계 §5-2 · §8 — 범위 밖 학생 · 겹침 · 다른 사람이 먼저 고침 등).
 */
import { fetchAPI } from '../../utils/apiUtils';
import { enrollmentLastDay, gradeLabel, tenureLastDay, withdrawalDay } from './periodDates';

const BASE = '/api/v2/academy';
const NETWORK_MSG = '서버에 연결할 수 없습니다. 인터넷 연결을 확인하거나 잠시 후 다시 시도해주세요.';

/**
 * 요청 한 번. 성공이면 봉투의 data, 실패면 Error 에 { httpStatus, reason, data, envelope } 를 실어 던진다.
 * envelope — 응답이 표준 봉투({status,…})였는가. 배포 전 서버는 경로가 없어 봉투 없는 404·405 를 준다.
 */
async function call(path, { method = 'GET', body, signal = null } = {}, getAccessToken, fallbackMsg) {
  const token = await getAccessToken();
  if (!token) {
    window.location.href = '/signin';
    const err = new Error(fallbackMsg);
    err.httpStatus = 401;
    throw err;
  }
  let response;
  try {
    response = await fetchAPI(`${BASE}${path}`, {
      method,
      headers: { Authorization: token, 'Content-Type': 'application/json' },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
  } catch (netErr) {
    // 취소는 네트워크 오류가 아니다 — 부른 쪽이 이미 버린 요청이라 그대로 넘겨 조용히 끝나게 한다
    if (netErr?.name === 'AbortError') throw netErr;
    throw new Error(NETWORK_MSG);
  }
  if (response.status === 401) {
    // 공용 유틸과 같은 뒷정리 — 죽은 토큰을 남기면 다음 화면이 또 401 을 맞는다
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.location.href = '/signin';
    const err = new Error(fallbackMsg);
    err.httpStatus = 401;
    throw err;
  }
  let payload = null;
  try { payload = await response.json(); } catch (_) { /* 아래에서 상태 코드로 표면화 */ }
  const envelope = payload != null && typeof payload.status === 'string';
  if (!response.ok || !envelope || payload.status !== 'success') {
    const err = new Error((envelope && payload.message) || `${fallbackMsg} (${response.status})`);
    err.httpStatus = response.status;
    err.envelope = envelope;
    // 서버가 봉투에 실어 보낸 문구 — 없으면 null. err.message 는 그것이 없을 때 화면 쪽 대신 문구로 채워지므로
    // 「서버 문구 우선」 판정(describeAcademyError)은 이 칸을 본다
    err.serverMessage = envelope && typeof payload.message === 'string' && payload.message.trim() ? payload.message : null;
    err.routeMissing = isRouteMissing(response.status, payload);
    err.data = envelope ? (payload.data ?? null) : null;
    err.reason = err.data?.reason ?? null;
    throw err;
  }
  return payload.data;
}

/**
 * 서버에 그 경로가 아직 없는가(배포 전). 두 모양이 있다:
 *   ① 봉투 없는 404·405 — 강의 v2 배포 전 서버가 주던 모양(syllabusApi isPredeployError)
 *   ② 서버의 기본 「경로 없음」 응답 — 봉투처럼 status 가 있지만 data 칸이 없고 문구가 고정이다.
 *      개발 서버 실측(2026-10-10, GET /api/v2/academy/settings):
 *      404 {"message": "The requested URL was not found on the server. …", "status": "error", "status_code": "404"}
 *      ②를 오류로 읽으면 미배포 안내 대신 영어 오류가 서고, 개발 서버의 목업 자동 연결도 서지 않는다.
 * 학생 없음 같은 진짜 404 는 서버가 data 칸을 실어 보낸다 — 그것은 미배포가 아니다.
 */
export function isRouteMissing(httpStatus, payload) {
  if (httpStatus !== 404 && httpStatus !== 405) return false;
  const envelope = payload != null && typeof payload.status === 'string';
  if (!envelope) return true;
  return !('data' in payload) && /requested URL was not found|method is not allowed/i.test(String(payload.message || ''));
}

const qs = (params) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v == null || v === false) return;
    const s = Array.isArray(v) ? v.join(',') : String(v).trim();
    if (s !== '') p.append(k, s);
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

const arr = (v) => (Array.isArray(v) ? v : []);
/** 목록 응답 — 페이지 봉투({items,…})든 배열이든 배열로 */
const listOf = (d) => (Array.isArray(d) ? d : arr(d?.items));

const pageOf = (d, page, perPage, norm = (x) => x) => {
  const items = listOf(d).map(norm);
  return {
    items,
    page: d?.page ?? page,
    perPage: d?.per_page ?? perPage,
    totalItems: Number.isInteger(d?.total_items) ? d.total_items : items.length,
    totalPages: Number.isInteger(d?.total_pages) ? Math.max(1, d.total_pages) : 1,
  };
};

/** 코드 칸의 이름 — 서버가 이름을 함께 실으면({code,label} · *_label) 그것을, 아니면 코드를 */
const labelOf = (obj, key) => {
  if (!obj) return '';
  const nested = obj[key];
  if (nested && typeof nested === 'object') return nested.label ?? nested.name ?? nested.code ?? '';
  return obj[`${key}_label`] ?? obj[`${key}_code`] ?? (typeof nested === 'string' ? nested : '');
};

// ── 화면 모양으로 바꾸기 ──

const normPerson = (p) => (p ? { staff_id: p.staff_id ?? null, name: p.name ?? '' } : null);

/** 학생이 든 반 하나 — S1 항목의 반 목록 · S2 current.classes */
export const normClassRef = (c) => ({
  enrollment_id: c.enrollment_id ?? null,
  class_id: c.class_id ?? null,
  class_name: c.class_name ?? c.name ?? '',
  start_on: c.start_on ?? null,
  end_on: c.end_on ?? null,
  last_day: enrollmentLastDay(c),
  teacher: normPerson(c.teacher),
});

/** S1 목록 한 줄 */
export const normStudentRow = (r) => ({
  // 화면 이동용 내부 번호 — 사람에게는 학생번호(student_no)를 보인다(백엔드 2단계 전달 §4-C)
  student_id: Number(r.student_id),
  student_name: r.student_name ?? r.name ?? '',
  // 이름 아래 이메일 — 학습 v2 목록과 같은 자리(오너 2026-10-10). 요청서 S1 항목에는 아직 없는 칸
  student_email: r.student_email ?? r.profile?.student_email ?? '',
  // 학생번호 4자리(글자) — 학원 출결 카드번호와 같은 값. 없으면 null(백엔드 2단계 전달 §2)
  student_no: r.student_no ?? null,
  is_blocked: !!r.is_blocked,
  is_deleted: !!r.is_deleted,
  is_test: !!r.is_test,
  state: r.state ?? 'none',
  withdraw_scheduled_on: r.withdraw_scheduled_on ?? null,
  withdraw_last_day: withdrawalDay(r),
  tenure: r.tenure ? { ...r.tenure, last_day: tenureLastDay(r.tenure) } : null,
  classes: arr(r.classes).map(normClassRef),
  school: r.school ?? null,
  grade: r.grade ?? null,
  grade_label: r.grade_label || gradeLabel(r.grade),
  needs_school_update: !!r.needs_school_update,
  department: r.department ?? null,
  last_note_on: r.last_note_on ?? null,
});

const normEnrollment = (e) => ({
  id: e.id ?? e.enrollment_id ?? null,
  class_id: e.class_id ?? null,
  class_name: e.class_name ?? e.class?.name ?? '',
  start_on: e.start_on ?? null,
  end_on: e.end_on ?? null,
  last_day: enrollmentLastDay(e),
  end_kind: e.end_kind ?? null,
  end_reason: labelOf(e, 'end_reason'),
  transfer_to_class_name: e.transfer_to?.class_name ?? e.transfer_to_class_name ?? null,
  transfer_from_class_name: e.transfer_from?.class_name ?? e.transfer_from_class_name ?? null,
  // 편성 기간 동안의 담임 — 담임이 바뀌었으면 여럿(설계 §4 반 편성 탭 「강사 김 → 이」)
  teachers: arr(e.teachers).length ? arr(e.teachers).map(normPerson) : (e.teacher ? [normPerson(e.teacher)] : []),
  estimated: !!(e.is_date_estimated ?? e.estimated),
  // 퇴원 예정 때문에 퇴원일에 저절로 닫힌 편성(end_kind WITHDRAW) — 퇴원일보다 앞 날짜로 퇴반 · 반 이동할 수 있다(백엔드 4단계 전달 §2)
  auto_closed: !!e.auto_closed,
});

/**
 * S2 profile · S4 응답 profile — 학생 정보 수정 창이 쓰는 칸(백엔드 2단계 전달 §2).
 * 연락처는 없으면 빈 글자, 학생번호는 없으면 null. is_blocked = 로그인 잠김(비밀번호를 새로 정하면 풀림).
 */
const normProfile = (p, d = {}) => ({
  student_name: p?.student_name ?? d.student_name ?? '',
  student_email: p?.student_email ?? '',
  mobile_number: p?.mobile_number ?? '',
  parent_mobile_number: p?.parent_mobile_number ?? '',
  student_no: p?.student_no ?? d.student_no ?? null,
  is_blocked: !!p?.is_blocked,
  is_deleted: !!p?.is_deleted,
  is_test: !!p?.is_test,
});

/** S2 학생 상세 묶음(요청서 §7-12 예시) */
export const normStudentDetail = (d) => {
  const cur = d?.current || {};
  const profile = normProfile(d?.profile, d || {});
  return {
    student_id: Number(d?.student_id),
    student_name: profile.student_name,
    student_email: profile.student_email,
    student_no: profile.student_no,
    profile,
    as_of: d?.as_of ?? null,
    current: {
      state: cur.state ?? 'none',
      withdraw_scheduled_on: cur.withdraw_scheduled_on ?? null,
      withdraw_last_day: withdrawalDay(cur),
      tenure: cur.tenure ? { ...cur.tenure, last_day: tenureLastDay(cur.tenure) } : null,
      classes: arr(cur.classes).map(normClassRef),
      school: cur.school ?? null,
      grade: cur.grade ?? null,
      grade_label: cur.grade_label || gradeLabel(cur.grade),
      needs_school_update: !!cur.needs_school_update,
      department: cur.department ?? null,
      paused: !!cur.paused,
    },
    tenures: arr(d?.tenures).map((t) => ({
      id: t.id ?? null, seq: t.seq ?? null, start_on: t.start_on, end_on: t.end_on ?? null, last_day: tenureLastDay(t),
      withdraw_reason: labelOf(t, 'withdraw_reason'), estimated: !!(t.is_date_estimated ?? t.estimated),
      // [퇴원일 변경] 창이 지금 사유 · 메모를 미리 채운다 — 메모를 비워 보내면 「기타」 내용이 지워진다(백엔드 5단계 전달 §4-F)
      withdraw_reason_code: t.withdraw_reason?.code ?? t.withdraw_reason_code ?? null,
      withdraw_memo: t.withdraw_memo ?? '',
    })),
    // auto_closed — 퇴원(예정)일에 저절로 닫힌 휴원. 복귀가 아니다(백엔드 5단계 전달 §2)
    pauses: arr(d?.pauses).map((p) => ({
      id: p.id ?? null, start_on: p.start_on, end_on: p.end_on ?? null, reason: labelOf(p, 'reason'),
      memo: p.memo ?? '', auto_closed: !!p.auto_closed,
    })),
    enrollments: arr(d?.enrollments).map(normEnrollment),
    school_periods: arr(d?.school_periods).map((s) => ({
      id: s.id ?? null, start_on: s.start_on, end_on: s.end_on ?? null,
      school: s.school ?? null, grade_at_start: s.grade_at_start ?? null, grade_base_year: s.grade_base_year ?? null,
      // 학년 이름은 서버 것을 먼저(14 = 「비재학」 — 백엔드 2단계 전달 §2 · §4-D)
      grade_label: s.grade_label || gradeLabel(s.grade_at_start ?? s.grade),
      estimated: !!(s.is_date_estimated ?? s.estimated), source: s.source ?? null,
    })),
    department_periods: arr(d?.department_periods).map((p) => ({
      id: p.id ?? null, start_on: p.start_on, end_on: p.end_on ?? null,
      department: p.department ?? null, estimated: !!(p.is_date_estimated ?? p.estimated), source: p.source ?? null,
    })),
    // 강사에게는 서버가 빼고 준다(요청서 §10) — 없으면 null
    first_inquiry: d?.first_inquiry ?? null,
    revision: d?.revision ?? null,
  };
};

/** N1 메모 한 줄 */
export const normNote = (n) => ({
  id: n.id ?? n.note_id ?? null,
  student_id: n.student_id ?? null,
  class_id: n.class_id ?? null,
  class_name: n.class_name ?? n.class?.name ?? null,
  category: labelOf(n, 'category'),
  category_code: n.category?.code ?? n.category_code ?? '',
  body: n.body ?? '',
  occurred_on: n.occurred_on ?? null,
  is_issue: !!n.is_issue,
  is_pinned: !!n.is_pinned,
  author_name: n.author_name ?? n.author?.name ?? '',
  // 작성자 직원 번호 — 강사의 「본인 메모」 판정은 이름이 아니라 이것으로 한다(같은 이름의 직원이 있을 수 있다, 백엔드 8단계 전달 §4-D)
  author_staff_id: n.author_staff_id ?? null,
});

/** N4 이력 한 줄 — date·type·title·refs·actor·created_at·is_future·is_correction */
export const normHistoryItem = (h) => ({
  date: h.date ?? null,
  type: h.type ?? '',
  title: h.title ?? '',
  actor: typeof h.actor === 'string' ? h.actor : (h.actor?.name ?? h.actor_name ?? ''),
  is_future: !!h.is_future,
  is_correction: !!h.is_correction,
  refs: h.refs ?? null,
});

/**
 * C1 반 한 줄 — 기준일 담임·시간표·인원(강사에게는 인원이 빈 값, 요청서 §10).
 * 반에는 개강일·종강일이 없다 — 만든 날(created_on)부터 있고, 끝은 비활성화(active · inactive_at)뿐이다(백엔드 3단계 전달 §1 · §4-B).
 * opened_on · closed_on 은 서버가 늘 null 로 준다(칸 이름만 남은 것).
 */
export const normClass = (c) => ({
  class_id: c.class_id ?? c.id ?? null,
  name: c.name ?? c.class_name ?? '',
  department: c.department ?? null,
  active: c.active !== false,
  inactive_at: c.inactive_at ?? null,
  created_on: c.created_on ?? null,
  opened_on: c.opened_on ?? null,
  closed_on: c.closed_on ?? null,
  teacher: normPerson(c.teacher),
  teacher_start_on: c.teacher_start_on ?? c.teacher?.start_on ?? null,
  slots: arr(c.schedule?.slots ?? c.slots),
  member_count: Number.isInteger(c.member_count) ? c.member_count : (Number.isInteger(c.headcount) ? c.headcount : null),
});

// ── 학생(S1·S2) ──

/**
 * S1 학생 목록. state 는 여러 값을 쉼표로 보낸다 — 화면 기본 「전체」는 all 을 보낸다.
 * 「재원」은 enrolled·paused 두 값이 필요하다(서버 기본값도 같다, 백엔드 2단계 전달 §1).
 * q 는 이름 · 지금 학교 이름 부분 일치 + 학생번호 4자리 정확히 일치.
 */
export async function listStudents({
  asOf, state, q, departmentId, classId, teacherStaffId, schoolLevel, grade, needs, sort, page = 1, perPage = 50, signal = null,
} = {}, getAccessToken) {
  const d = await call(`/students${qs({
    as_of: asOf, state, q, department_id: departmentId, class_id: classId, teacher_staff_id: teacherStaffId,
    school_level: schoolLevel, grade, needs, sort, page, per_page: perPage,
  })}`, { signal }, getAccessToken, '학생 목록을 불러오지 못했습니다.');
  return { ...pageOf(d, page, perPage, normStudentRow), scope: d?.scope ?? null, asOf: d?.as_of ?? asOf ?? null };
}

/** S1 을 끝까지 — 강사 통계(내 반 학생 수·퇴원 예정)는 범위 학생 전부가 필요하다. per_page 상한 200(§1-7) */
export async function listAllStudents(params = {}, getAccessToken) {
  const first = await listStudents({ ...params, page: 1, perPage: 200 }, getAccessToken);
  const items = [...first.items];
  for (let page = 2; page <= first.totalPages; page += 1) {
    const part = await listStudents({ ...params, page, perPage: 200 }, getAccessToken);
    items.push(...part.items);
  }
  return { ...first, items };
}

/** S2 학생 상세 */
export async function getStudent(studentId, { asOf, signal = null } = {}, getAccessToken) {
  const d = await call(`/students/${studentId}${qs({ as_of: asOf })}`, { signal }, getAccessToken, '학생 정보를 불러오지 못했습니다.');
  return normStudentDetail(d);
}

// ── 메모·이력(N1·N4) ──

export async function listNotes({
  studentId, classId, academy, category, issueOnly, from, to, page = 1, perPage = 50, signal = null,
} = {}, getAccessToken) {
  const d = await call(`/notes${qs({
    student_id: studentId, class_id: classId, academy: academy ? 1 : null, category, issue_only: issueOnly ? 1 : null,
    from, to, page, per_page: perPage,
  })}`, { signal }, getAccessToken, '메모를 불러오지 못했습니다.');
  return pageOf(d, page, perPage, normNote);
}

/** 학원·모든 반의 메모를 고정·날짜·번호 내림차순으로 합친 뒤 페이지를 나눈다. 학생 메모는 각 N1 대상에서 제외된다. */
export async function listAcademyNotes({ from, to, page = 1, perPage = 50, signal = null } = {}, getAccessToken) {
  if (!Number.isInteger(page) || page < 1 || page > 100000 || !Number.isInteger(perPage) || perPage < 1 || perPage > 200) {
    throw new Error('메모 페이지 또는 페이지 크기가 올바르지 않습니다.');
  }
  const classes = await listClasses({ state: 'all', signal }, getAccessToken);
  const classNames = new Map(classes.map((cls) => [cls.class_id, cls.name]));
  const targets = [{ academy: true }, ...classes.map((cls) => ({ classId: cls.class_id }))];
  const needed = page * perPage;
  const sourcePageSize = Math.min(needed, 200);
  const items = [];
  let totalItems = 0;
  for (let start = 0; start < targets.length; start += 4) {
    // 전역 페이지의 끝 순위까지 대상별로 읽어야 다음 페이지에서도 메모가 빠지거나 중복되지 않는다.
    // eslint-disable-next-line no-await-in-loop
    const results = await Promise.all(targets.slice(start, start + 4).map(async (target) => {
      const params = { ...target, from, to, perPage: sourcePageSize, signal };
      const first = await listNotes(params, getAccessToken);
      const collected = [...first.items];
      const lastPage = Math.min(first.totalPages, Math.ceil(needed / sourcePageSize));
      for (let sourcePage = 2; sourcePage <= lastPage; sourcePage += 1) {
        // eslint-disable-next-line no-await-in-loop
        const next = await listNotes({ ...params, page: sourcePage }, getAccessToken);
        collected.push(...next.items);
      }
      return { items: collected.slice(0, needed), totalItems: first.totalItems };
    }));
    items.push(...results.flatMap((result) => result.items));
    totalItems += results.reduce((sum, result) => sum + result.totalItems, 0);
  }
  items.sort((a, b) => Number(b.is_pinned) - Number(a.is_pinned)
    || (b.occurred_on || '').localeCompare(a.occurred_on || '') || Number(b.id) - Number(a.id));
  return { page, perPage, totalItems, totalPages: Math.max(1, Math.ceil(totalItems / perPage)), items: items.slice((page - 1) * perPage, needed).map((note) => ({
    ...note, class_name: note.class_name || classNames.get(note.class_id) || null,
  })) };
}

/** 첫 화면도 상세의 전체 조회와 같은 대상·정렬을 쓴다. */
export function getNotesPreview({ signal = null } = {}, getAccessToken) {
  return listAcademyNotes({ perPage: 3, signal }, getAccessToken);
}

export async function getStudentHistory(studentId, { types, from, to, page = 1, perPage = 200, signal = null } = {}, getAccessToken) {
  const d = await call(`/students/${studentId}/history${qs({ types, from, to, page, per_page: perPage })}`,
    { signal }, getAccessToken, '이력을 불러오지 못했습니다.');
  return pageOf(d, page, perPage, normHistoryItem);
}

// ── 통계(ST1·ST2) — 매니저 전용. 수강 기간(ST5) · 확인 필요(ST8)는 만들지 않는다(오너 결정 2026-10-13, 백엔드 6단계 전달 §1) ──

const num = (v) => (Number.isFinite(Number(v)) && v !== null && v !== '' ? Number(v) : null);

/** ST1 첫 화면 숫자. 이번 달 값은 오늘까지 확정분과 내일 이후 예정분(scheduled_*)을 나눠 받는다 */
export async function getStatsSummary({ asOf, signal = null } = {}, getAccessToken) {
  const d = await call(`/stats/summary${qs({ as_of: asOf })}`, { signal }, getAccessToken, '현황 숫자를 불러오지 못했습니다.');
  const m = d?.month || {};
  return {
    asOf: d?.as_of ?? asOf ?? null,
    headcount: num(d?.headcount?.total ?? d?.headcount),
    paused: num(d?.headcount?.paused ?? d?.paused),
    month: {
      from: m.from ?? null,
      to: m.to ?? null,
      registeredNew: num(m.registered_new),
      registeredAgain: num(m.registered_again),
      withdrawn: num(m.withdrawn),
      withdrawRate: num(m.withdraw_rate),
      opening: num(m.opening),
      stopped: num(m.stopped),
      stoppedPause: num(m.stopped_pause),
      stoppedWithdraw: num(m.stopped_withdraw),
      scheduledStopped: num(m.scheduled_stopped),
      activeOpening: num(m.active_opening),
      activeClosing: num(m.active_closing),
      activeIn: num(m.active_in),
      resumed: num(m.resumed),
      scheduledRegistered: (num(m.scheduled_registered_new) ?? 0) + (num(m.scheduled_registered_again) ?? 0),
      scheduledWithdrawn: num(m.scheduled_withdrawn) ?? 0,
    },
    withdrawScheduled30: num(d?.withdraw_scheduled_30d),
    openInquiries: num(d?.open_inquiries),
    monthNewInquiries: num(d?.month_new_inquiries),
    needsAttention: num(d?.needs_attention),
  };
}

/** ST2 기준일 인원 — group_by(학년·부서·학교 …), split_by 를 주면 교차표 */
export async function getHeadcount({ asOf, groupBy = 'none', splitBy, signal = null } = {}, getAccessToken) {
  const d = await call(`/stats/headcount${qs({ as_of: asOf, group_by: groupBy, split_by: splitBy })}`,
    { signal }, getAccessToken, '인원 통계를 불러오지 못했습니다.');
  const normGroup = (g) => ({
    key: g.key ?? null,
    label: g.label ?? String(g.key ?? ''),
    count: num(g.count) ?? 0,
    paused: num(g.paused) ?? 0,
    splits: arr(g.splits).map((x) => ({ key: x.key ?? null, label: x.label ?? String(x.key ?? ''), count: num(x.count) ?? 0, paused: num(x.paused) ?? 0 })),
  });
  return {
    asOf: d?.as_of ?? asOf ?? null,
    groups: arr(d?.groups).map(normGroup),
    totalDistinct: num(d?.total_distinct),
    unassigned: num(d?.unassigned?.count ?? d?.unassigned) ?? 0,
    dataReliableFrom: d?.data_reliable_from ?? null,
  };
}

// ── 캘린더 · 확장 통계 — 백엔드 6b 계약. dev 배포 완료. ──

const reportArray = (data, key) => {
  if (!Array.isArray(data?.[key])) throw new Error('받은 데이터 형식이 올바르지 않습니다. 다시 불러와 주세요.');
  return data[key];
};

export async function getCalendar({ from, to, types, signal = null }, getAccessToken) {
  const d = await call(`/calendar${qs({ from, to, types })}`, { signal }, getAccessToken, '캘린더를 불러오지 못했습니다.');
  const days = reportArray(d, 'days');
  if (days.some((day) => !day || typeof day.date !== 'string' || !Array.isArray(day.items)
    || day.items.some((item) => !item || typeof item.type !== 'string' || (item.title != null && typeof item.title !== 'string')))) {
    throw new Error('받은 일정 형식이 올바르지 않습니다. 다시 불러와 주세요.');
  }
  return { ...d, days };
}

export async function getFlows({ from, to, bucket = 'month', groupBy = 'none', signal = null }, getAccessToken) {
  const d = await call(`/stats/flows${qs({ from, to, bucket, group_by: groupBy, denominator: 'opening' })}`,
    { signal }, getAccessToken, '입원·퇴원 통계를 불러오지 못했습니다.');
  const buckets = reportArray(d, 'buckets');
  if (buckets.some((item) => !item || typeof item.start !== 'string' || typeof item.end !== 'string'
    || !item.total || typeof item.total !== 'object' || !Array.isArray(item.groups)
    || item.groups.some((group) => !group || typeof group.label !== 'string'))) {
    throw new Error('받은 통계 형식이 올바르지 않습니다. 다시 불러와 주세요.');
  }
  return { ...d, buckets };
}

export async function getWithdrawScheduled({ from, to, signal = null }, getAccessToken) {
  const d = await call(`/stats/withdraw-scheduled${qs({ from, to })}`, { signal }, getAccessToken, '퇴원 예정 목록을 불러오지 못했습니다.');
  const items = reportArray(d, 'items');
  if (items.some((item) => !item || !Number.isInteger(item.student_id) || typeof item.student_name !== 'string' || typeof item.end_on !== 'string'
    || (item.classes != null && (!Array.isArray(item.classes) || item.classes.some((cls) => !cls || typeof cls.class_name !== 'string'))))) {
    throw new Error('받은 퇴원 예정 형식이 올바르지 않습니다. 다시 불러와 주세요.');
  }
  return { ...d, items };
}

const countOk = (n) => Number.isInteger(n) && n >= 0;
const admissionCountsOk = (row) => row && ['consulted', 'admitted', 'not_admitted'].every((key) => countOk(row[key]))
  && row.consulted === row.admitted + row.not_admitted
  && (row.consulted === 0 ? row.admission_rate === null : typeof row.admission_rate === 'number' && row.admission_rate >= 0 && row.admission_rate <= 1);
function checkAdmissionCoverage(d) {
  const c = d?.coverage;
  if (!c || !['complete', 'partial'].includes(c.status) || typeof c.data_reliable_from !== 'string'
    || !countOk(c.excluded_count) || !countOk(c.unknown_created_at_total)
    || !['from', 'to', 'as_of'].every((key) => typeof d[key] === 'string')) {
    throw new Error('받은 입학률 형식이 올바르지 않습니다. 다시 불러와 주세요.');
  }
}

/** 상담 유입 월 기준. 비율·조회 기간·신뢰 범위는 서버 값을 그대로 사용한다. */
export async function getAdmissions({ from, to, asOf, signal = null }, getAccessToken) {
  const d = await call(`/stats/admissions${qs({ from, to, as_of: asOf, bucket: 'month' })}`, { signal }, getAccessToken, '입학률을 불러오지 못했습니다.');
  checkAdmissionCoverage(d);
  if (!admissionCountsOk(d.total) || d.bucket !== 'month' || !Array.isArray(d.buckets)
    || d.buckets.some((row) => !admissionCountsOk(row) || !['month', 'from', 'to'].every((key) => typeof row[key] === 'string'))) {
    throw new Error('받은 입학률 형식이 올바르지 않습니다. 다시 불러와 주세요.');
  }
  return d;
}

/** 삭제된 계정도 집계 대상. 목록에서 임의로 걸러 내지 않는다. */
export async function getAdmissionStudents({ from, to, asOf, admissionStatus = 'all', page = 1, perPage = 50, signal = null }, getAccessToken) {
  const d = await call(`/stats/admissions/students${qs({ from, to, as_of: asOf, admission_status: admissionStatus, page, per_page: perPage })}`,
    { signal }, getAccessToken, '입학률 학생 명단을 불러오지 못했습니다.');
  checkAdmissionCoverage(d);
  const statuses = ['all', 'admitted', 'not_admitted'];
  if (!statuses.includes(d.admission_status) || !['page', 'per_page', 'total_items', 'total_pages'].every((key) => countOk(d[key]))
    || d.page < 1 || d.per_page < 1 || !Array.isArray(d.items)
    || d.items.some((row) => !row || !Number.isInteger(row.student_id) || typeof row.student_name !== 'string'
      || typeof row.created_at !== 'string' || !Number.isFinite(Date.parse(row.created_at))
      || !(row.first_enrollment_confirmed_at === null || (typeof row.first_enrollment_confirmed_at === 'string' && Number.isFinite(Date.parse(row.first_enrollment_confirmed_at))))
      || !statuses.slice(1).includes(row.admission_status) || typeof row.is_deleted !== 'boolean')) {
    throw new Error('받은 입학률 학생 명단 형식이 올바르지 않습니다. 다시 불러와 주세요.');
  }
  return d;
}

// ── 반 배정 기록 · 활동 기록(백엔드 6단계 전달 §2-2 · §2-3) — 매니저 · 관리자 전용(강사 403) ──

/** 이동 짝의 상대 편성 — { enrollment_id, class_id, class_name } 또는 null */
const normEnrollmentLink = (x) => (x ? { enrollment_id: x.enrollment_id ?? null, class_id: x.class_id ?? null, class_name: x.class_name ?? '' } : null);

/**
 * 반 배정 기록 한 줄 — 퇴반일(last_day)은 마지막 수강일, end_on은 저장 경계(그 반이 아닌 첫날). 반을 옮겨도 앞 반 줄이 남는다.
 * end_kind: TRANSFER(반 이동) · OTHER(퇴반, end_reason 에 사유) · WITHDRAW(퇴원으로 저절로 닫힘, auto_closed) · null(진행 중)
 */
const normEnrollmentRecord = (e) => ({
  enrollment_id: e.enrollment_id ?? e.id ?? null,
  student_id: e.student_id ?? null,
  student_name: e.student_name ?? '',
  class_id: e.class_id ?? null,
  class_name: e.class_name ?? '',
  start_on: e.start_on ?? null,
  end_on: e.end_on ?? null,
  last_day: enrollmentLastDay(e),
  end_kind: e.end_kind ?? null,
  end_reason: labelOf(e, 'end_reason'),
  transfer_from: normEnrollmentLink(e.transfer_from),
  transfer_to: normEnrollmentLink(e.transfer_to),
  auto_closed: !!e.auto_closed,
  memo: e.memo ?? '',
});

/** 반 배정 기록(GET /enrollments) — 반 배정일 최신순, 지운 편성 제외, 합산 없음. from · to 는 그 기간에 걸친 편성 */
export async function listEnrollmentRecords({
  classId, studentId, from, to, page = 1, perPage = 50, signal = null,
} = {}, getAccessToken) {
  const d = await call(`/enrollments${qs({ class_id: classId, student_id: studentId, from, to, page, per_page: perPage })}`,
    { signal }, getAccessToken, '반 배정 기록을 불러오지 못했습니다.');
  return pageOf(d, page, perPage, normEnrollmentRecord);
}

/** 활동 기록 한 항목 — 한 번 저장 = 한 항목(반 이동은 두 줄이 한 항목). label · rows[].text 는 서버가 지은 한글 */
const normChangeLog = (x) => ({
  request_id: x.request_id ?? null,
  occurred_at: x.occurred_at ?? null,
  actor: x.actor ? { staff_id: x.actor.staff_id ?? null, name: x.actor.name ?? '', role: x.actor.role ?? null } : null,
  source: x.source ?? null,
  op: x.op ?? null,
  label: x.label ?? '',
  students: arr(x.students).map((st) => ({ student_id: st.student_id ?? null, student_name: st.student_name ?? '' })),
  classes: arr(x.classes).map((c) => ({ class_id: c.class_id ?? null, class_name: c.class_name ?? '' })),
  reason: x.reason ?? null,
  rows: arr(x.rows).map((r) => ({
    entity_type: r.entity_type ?? null, entity_label: r.entity_label ?? '', entity_id: r.entity_id ?? null,
    action: r.action ?? null, action_label: r.action_label ?? '',
    student_id: r.student_id ?? null, class_id: r.class_id ?? null,
    changes: r.changes && typeof r.changes === 'object' ? r.changes : {},
    text: r.text ?? '', reason: r.reason ?? null,
  })),
});

/**
 * 활동 기록(GET /change-logs) — 최신순. from · to 는 한국 날짜(안 주면 오늘, to 만 주면 그날 하루).
 * actorId 는 직원 staff_id, kind 는 student · class · note · reference. 받은 기간(from · to)도 돌려준다
 */
export async function listChangeLogs({
  from, to, actorId, studentId, classId, kind, page = 1, perPage = 50, signal = null,
} = {}, getAccessToken) {
  const d = await call(`/change-logs${qs({
    from, to, actor_id: actorId, student_id: studentId, class_id: classId, kind, page, per_page: perPage,
  })}`, { signal }, getAccessToken, '활동 기록을 불러오지 못했습니다.');
  return { ...pageOf(d, page, perPage, normChangeLog), from: d?.from ?? from ?? null, to: d?.to ?? to ?? null };
}

// ── 반·직원·부서(C1·E1·E9·B1) — 설정(A4 · A5)은 없다: 일괄 이관이 없어 서버가 /settings 를 열지 않는다(백엔드 4단계 전달 §1) ──

export async function listClasses({ state = 'open', asOf, departmentId, teacherStaffId, q, signal = null } = {}, getAccessToken) {
  const d = await call(`/classes${qs({ state, as_of: asOf, department_id: departmentId, teacher_staff_id: teacherStaffId, q })}`,
    { signal }, getAccessToken, '반 목록을 불러오지 못했습니다.');
  return listOf(d).map(normClass);
}

/** E1 직원 목록 — 강사가 부르면 본인 것만 온다(요청서 §10). 강사 화면은 이것으로 「내 직원 번호」를 안다 */
export async function listStaff({ kind, employment = 'current', asOf, q, signal = null } = {}, getAccessToken) {
  const d = await call(`/staff${qs({ kind, employment, as_of: asOf, q })}`, { signal }, getAccessToken, '직원 목록을 불러오지 못했습니다.');
  return listOf(d).map((s) => ({ staff_id: s.staff_id ?? s.id ?? null, name: s.name ?? '', kind: s.kind ?? null }));
}

/** E9 직원 일정 — [start_on, end_on), 하루짜리 연차의 종료일은 다음 날(T7) */
export async function listStaffSchedules({ from, to, staffId, kind, signal = null } = {}, getAccessToken) {
  const d = await call(`/staff-schedules${qs({ from, to, staff_id: staffId, kind })}`,
    { signal }, getAccessToken, '일정을 불러오지 못했습니다.');
  return listOf(d).map((s) => ({
    id: s.id ?? null, staff_id: s.staff_id ?? null, kind: labelOf(s, 'kind'),
    start_on: s.start_on, end_on: s.end_on, title: s.title ?? '',
  }));
}

export async function listDepartments({ includeArchived = false, asOf, signal = null } = {}, getAccessToken) {
  const d = await call(`/departments${qs({ include_archived: includeArchived ? 1 : null, as_of: asOf })}`,
    { signal }, getAccessToken, '부서 목록을 불러오지 못했습니다.');
  return listOf(d)
    .map((x) => ({
      department_id: x.department_id ?? x.id ?? null, name: x.name ?? '', sort_order: x.sort_order ?? 0,
      school_levels: Array.isArray(x.school_levels) ? x.school_levels : (x.school_level ? [x.school_level] : []), member_count: Number.isInteger(x.member_count) ? x.member_count : null,
      archived: !!(x.archived || x.archived_at),
    }))
    .sort((a, b) => a.sort_order - b.sort_order || a.department_id - b.department_id);
}

// ── 쓰기 — 학생 기록(S3~S17) ──
// 학생 단위 쓰기는 모두 if_revision(학생 판 번호)을 싣는다(요청서 §7-0 · V15). 다르면 409 stale.
// 몸통 칸 이름은 요청서 §7-5 표를 따른다. 응답은 쓰지 않고, 부른 쪽이 상세(S2)를 다시 부른다.

const mutate = (path, method, body, getAccessToken, msg) => call(path, { method, body }, getAccessToken, msg);
const rev = (ifRevision) => (ifRevision != null ? { if_revision: ifRevision } : {});

/**
 * S3 새 학생 — 학생 앱 로그인 계정(이메일 · 비밀번호) + 학교·학년 + 부서. 등록일(startOn)을 주면 재원 기간과 고른 반도 같은 날 연다.
 * 등록일이 없으면(null) 학생 정보만 — 상태 「등록 전」(none), 학교·부서 기간은 만든 날부터(백엔드 2b 전달 §2). 그때는 start_on 키를 빼고
 * 반도 보내지 않는다(등록일 없이 반을 넣으면 400). 빈 글자 start_on 은 서버가 400 으로 막는다.
 * password · student_no(4자리, 선택)는 백엔드 2단계 전달 §4-A 로 더한 칸. 고른 반(enrollments)의 입반일은 등록일 —
 * 등록일이 반을 만든 날보다 앞이면 반을 만든 날부터. 한 반이라도 안 되면 학생도 만들어지지 않는다(백엔드 4단계 전달 §3).
 */
export function createStudent({
  studentName, mobileNumber, parentMobileNumber, studentEmail, password, studentNo, startOn, schoolId, grade, departmentId, classIds = [], isTest,
}, getAccessToken) {
  return mutate('/students', 'POST', {
    student_name: studentName, mobile_number: mobileNumber || null, parent_mobile_number: parentMobileNumber || null,
    // 키를 빼는 것은 등록일이 없을 때(null · undefined)만 — 빈 글자는 그대로 보내 서버가 400 으로 막게 한다(비운 실수로 정보만 학생이 생기지 않게)
    student_email: studentEmail || null, password, student_no: studentNo || null, ...(startOn != null ? { start_on: startOn } : {}),
    school_id: schoolId || null, grade, department_id: departmentId || null,
    ...(isTest !== undefined ? { is_test: isTest } : {}),
    enrollments: startOn != null ? classIds.map((class_id) => ({ class_id })) : [],
  }, getAccessToken, '학생을 만들지 못했습니다.');
}
/**
 * S4 학생 정보 수정(백엔드 2단계 전달 §4-B) — 넘긴 칸만 싣는다(서버는 보낸 칸 중 바뀐 것만 검사·저장).
 * password 는 적었을 때만 · studentNo 가 null(또는 빈 글자)이면 학생번호 지우기. 기간 기록이 아니라 if_revision 은 없다.
 * 비밀번호를 바꾸면 로그인 잠금이 풀리고, 이메일을 바꾸면 학생은 새 이메일로 다시 로그인한다.
 */
export async function updateProfile(studentId, {
  studentName, studentEmail, password, mobileNumber, parentMobileNumber, studentNo, isTest,
} = {}, getAccessToken) {
  const d = await mutate(`/students/${studentId}/profile`, 'PATCH', {
    ...(studentName !== undefined ? { student_name: studentName } : {}),
    ...(studentEmail !== undefined ? { student_email: studentEmail } : {}),
    ...(password ? { password } : {}),
    ...(mobileNumber !== undefined ? { mobile_number: mobileNumber } : {}),
    ...(parentMobileNumber !== undefined ? { parent_mobile_number: parentMobileNumber } : {}),
    ...(studentNo !== undefined ? { student_no: studentNo || null } : {}),
    ...(isTest !== undefined ? { is_test: isTest } : {}),
  }, getAccessToken, '학생 정보를 수정하지 못했습니다.');
  return { student_id: Number(d?.student_id ?? studentId), profile: normProfile(d?.profile) };
}
/**
 * S5 등록·재등록 — 「등록 전」(학생 정보만 만든 학생 · v1 에만 있던 계정)도 같은 계정이 재원생이 된다. 등록일은 지난 퇴원일 이후 —
 * 재원 중 · 등록 예정이면 409 overlap + 겹친 재원 기간. 고른 반(classIds)은 새 학생과 같은 규칙으로 입반한다(백엔드 4단계 전달 §3)
 */
export const registerStudent = (id, { startOn, classIds = [], ifRevision }, gat) => mutate(`/students/${id}/register`, 'POST',
  { start_on: startOn, enrollments: classIds.map((class_id) => ({ class_id })), ...rev(ifRevision) }, gat, '등록하지 못했습니다.');
/** S6 퇴원 — 선택한 마지막 수강일을 그대로 보낸다. 오늘 포함 이후는 예정. +1 저장 변환은 서버만 한다. */
export const withdrawStudent = (id, { lastDay, reasonCode, memo, ifRevision }, gat) => mutate(`/students/${id}/withdraw`, 'POST',
  { last_day: lastDay, reason_code: reasonCode, memo: memo || null, ...rev(ifRevision) }, gat, '퇴원을 저장하지 못했습니다.');
/** S7 퇴원 예정 취소 */
export const cancelWithdraw = (id, { ifRevision }, gat) => mutate(`/students/${id}/withdraw/cancel`, 'POST', { ...rev(ifRevision) }, gat, '퇴원 예정을 취소하지 못했습니다.');
/** S8 휴원 시작 — 복귀 예정일은 없다(보내면 400). 시작일은 오늘까지, 사유 필수(백엔드 5단계 전달 §1 · §3) */
export const startPause = (id, { startOn, reasonCode, memo, ifRevision }, gat) => mutate(`/students/${id}/pauses`, 'POST',
  { start_on: startOn, reason_code: reasonCode || null, memo: memo || null, ...rev(ifRevision) }, gat, '휴원을 저장하지 못했습니다.');
/** S8 복귀 — 비어 있던 복귀일을 채우는 것은 사유 없이(V11 예외). 휴원 기간의 정정·삭제는 correctPeriod · deletePeriod('pause') */
export const updatePause = (pauseId, { startOn, endOn, reason, ifRevision }, gat) => mutate(`/student-pauses/${pauseId}`, 'PATCH',
  { ...(startOn ? { start_on: startOn } : {}), ...(endOn !== undefined ? { end_on: endOn } : {}), ...(reason ? { reason } : {}), ...rev(ifRevision) }, gat, '휴원 기록을 수정하지 못했습니다.');
/** S9 입반 */
export const enrollStudent = (id, { classId, startOn, ifRevision }, gat) => mutate(`/students/${id}/enrollments`, 'POST',
  { class_id: classId, start_on: startOn, ...rev(ifRevision) }, gat, '입반하지 못했습니다.');
/** S10 기타 퇴반(종료 이유 OTHER) */
// memo — 퇴원 · 휴원과 같은 모양으로 더한 칸(백엔드 1단계 전달 §4-E · 「기타」는 메모로). 서버는 4단계(퇴반)에서 받는다
export const endEnrollment = (enrollmentId, { lastDay, reasonCode, memo, ifRevision }, gat) => mutate(`/enrollments/${enrollmentId}/end`, 'POST',
  { last_day: lastDay, reason_code: reasonCode || null, memo: memo || null, ...rev(ifRevision) }, gat, '퇴반하지 못했습니다.');
/** S11 반 이동 — 같은 날 앞 반 퇴반 + 새 반 입반 */
export const transferStudent = (id, { fromEnrollmentId, toClassId, on, ifRevision }, gat) => mutate(`/students/${id}/transfer`, 'POST',
  { from_enrollment_id: fromEnrollmentId, to_class_id: toClassId, on, ...rev(ifRevision) }, gat, '반을 옮기지 못했습니다.');
/** S12 · S14 · S16 · S17 정정과 삭제 — 정정은 사유 필수(V11) */
const PERIOD_PATH = { enrollment: '/enrollments', school: '/student-school-periods', department: '/student-department-periods', tenure: '/tenures', pause: '/student-pauses' };
export const correctPeriod = (kind, periodId, fields, { reason, ifRevision }, gat) => mutate(`${PERIOD_PATH[kind]}/${periodId}`, 'PATCH',
  { ...fields, reason, ...rev(ifRevision) }, gat, '기록을 정정하지 못했습니다.');
export const deletePeriod = (kind, periodId, { reason, ifRevision }, gat) => mutate(`${PERIOD_PATH[kind]}/${periodId}`, 'DELETE',
  { reason, ...rev(ifRevision) }, gat, '기록을 지우지 못했습니다.');
/** S13 학교·학년 변경 — 앞 기간을 시작일로 닫음 */
export const changeSchool = (id, { schoolId, grade, startOn, ifRevision }, gat) => mutate(`/students/${id}/school-periods`, 'POST',
  { school_id: schoolId || null, grade, start_on: startOn, ...rev(ifRevision) }, gat, '학교·학년을 바꾸지 못했습니다.');
/** S15 부서 변경 */
export const changeDepartment = (id, { departmentId, startOn, ifRevision }, gat) => mutate(`/students/${id}/department-periods`, 'POST',
  { department_id: departmentId, start_on: startOn, ...rev(ifRevision) }, gat, '부서를 바꾸지 못했습니다.');

// ── 쓰기 — 메모(N2 · N3) ──
export const createNote = ({ studentId, classId, categoryCode, body, occurredOn, isIssue, isPinned }, gat) => mutate('/notes', 'POST', {
  student_id: studentId ?? null, class_id: classId ?? null, category_code: categoryCode, body,
  occurred_on: occurredOn || null, is_issue: !!isIssue, is_pinned: !!isPinned,
}, gat, '메모를 저장하지 못했습니다.');
export const updateNote = (noteId, fields, gat) => mutate(`/notes/${noteId}`, 'PATCH', {
  ...(fields.categoryCode ? { category_code: fields.categoryCode } : {}), ...(fields.body !== undefined ? { body: fields.body } : {}),
  ...(fields.occurredOn ? { occurred_on: fields.occurredOn } : {}), ...(fields.isIssue !== undefined ? { is_issue: !!fields.isIssue } : {}),
  ...(fields.isPinned !== undefined ? { is_pinned: !!fields.isPinned } : {}),
}, gat, '메모를 수정하지 못했습니다.');
export const deleteNote = (noteId, gat) => mutate(`/notes/${noteId}`, 'DELETE', undefined, gat, '메모를 지우지 못했습니다.');

// ── 반(C2 · C3 · C4~C12) · 부서(B2) · 학교(B4) · 코드(A1) ──

const normPeriodPerson = (t) => ({ id: t.id ?? null, staff_id: t.staff_id ?? t.teacher?.staff_id ?? null, name: t.name ?? t.teacher?.name ?? '', start_on: t.start_on, end_on: t.end_on ?? null });

/** C2 반 상세 — 담임 기간 전부 · 시간표 판 전부 · 기준일 학생(강사에게는 범위 학생만) */
export async function getClass(classId, { asOf, signal = null } = {}, getAccessToken) {
  const d = await call(`/classes/${classId}${qs({ as_of: asOf })}`, { signal }, getAccessToken, '반 정보를 불러오지 못했습니다.');
  return {
    ...normClass(d || {}),
    memo: d?.memo ?? '',
    revision: d?.revision ?? null,
    teachers: arr(d?.teachers).map(normPeriodPerson),
    schedules: arr(d?.schedules).map((sc) => ({ id: sc.id ?? null, start_on: sc.start_on, end_on: sc.end_on ?? null, slots: arr(sc.slots) })),
    members: arr(d?.members).map((m) => ({
      student_id: m.student_id, student_name: m.student_name ?? '', enrollment_id: m.enrollment_id ?? null,
      start_on: m.start_on ?? null, end_on: m.end_on ?? null, last_day: enrollmentLastDay(m), state: m.state ?? 'enrolled',
    })),
  };
}
/** C4 반 만들기 — 반은 만든 날(오늘)부터 있다. 개강일은 없다(백엔드 3단계 전달 §1). 반 이름은 비활성 반을 포함해 겹치면 409 duplicate */
export const createClass = ({ name, departmentId, teacherStaffId, slots }, gat) => mutate('/classes', 'POST', {
  name, department_id: departmentId || null, teacher: { staff_id: teacherStaffId }, schedule: { slots },
}, gat, '반을 만들지 못했습니다.');
/** C5 반 수정 — 이름 · 부서(null = 부서 없음) · 메모. 개강일 · 정정 사유는 없다 */
export const updateClass = (classId, { name, departmentId, memo, ifRevision }, gat) => mutate(`/classes/${classId}`, 'PATCH', {
  ...(name !== undefined ? { name } : {}), ...(departmentId !== undefined ? { department_id: departmentId || null } : {}),
  ...(memo !== undefined ? { memo } : {}), ...rev(ifRevision),
}, gat, '반 정보를 수정하지 못했습니다.');
/**
 * C5 비활성화 · 다시 활성화 — 종강 · 종강 취소 대신(백엔드 3단계 전달 §4-A). 반은 지우지 않는다.
 * 비활성 반은 기본 목록에서 빠지고 담임 · 시간표를 바꿀 수 없다. 학생이 남은 반(입반 예정 포함)은 비활성화할 수 없다(409 in_use
 * + student_count — 백엔드 4단계 전달 §3). 부서가 감춰진 반은 다시 활성화할 수 없다(409 in_use) — 같은 요청에
 * departmentId: null 을 실으면 부서를 비우고 활성화한다(백엔드 3단계 전달 §3 · 4단계 전달 §4-G).
 */
export const setClassActive = (classId, { active, departmentId, ifRevision }, gat) => mutate(`/classes/${classId}`, 'PATCH',
  { active: !!active, ...(departmentId !== undefined ? { department_id: departmentId } : {}), ...rev(ifRevision) },
  gat, active ? '반을 다시 활성화하지 못했습니다.' : '반을 비활성화하지 못했습니다.');
/**
 * C8 담임 교체 · C10 시간표 변경 — 바뀌는 날(기본 오늘)부터. 반을 만든 날보다 앞은 안 된다. 그날 시작한 기간이 있으면 덮어쓰고,
 * 뒤에 예정된 변경이 있으면 그 전날까지만 들어간다(백엔드 3단계 전달 §3)
 */
export const changeClassTeacher = (classId, { staffId, startOn, ifRevision }, gat) => mutate(`/classes/${classId}/teachers`, 'POST', { staff_id: staffId, start_on: startOn, ...rev(ifRevision) }, gat, '담임을 바꾸지 못했습니다.');
export const changeClassSchedule = (classId, { startOn, slots, ifRevision }, gat) => mutate(`/classes/${classId}/schedules`, 'POST', { start_on: startOn, slots, ...rev(ifRevision) }, gat, '시간표를 바꾸지 못했습니다.');
/** C12 여러 학생 한 번에 입반 — 전부 성공 또는 전부 취소, 실패면 data.failures[](학생별 사유) */
export const addClassMembers = (classId, { studentIds, startOn, ifRevision }, gat) => mutate(`/classes/${classId}/members`, 'POST', { student_ids: studentIds, start_on: startOn, ...rev(ifRevision) }, gat, '학생을 입반하지 못했습니다.');
export const createDepartment = ({ name, schoolLevels = [], sortOrder }, gat) => mutate('/departments', 'POST', { name, school_levels: schoolLevels, sort_order: sortOrder ?? 0 }, gat, '부서를 만들지 못했습니다.');

/** B4 학교 검색 */
export async function listSchools({ q, level, signal = null } = {}, getAccessToken) {
  const d = await call(`/schools${qs({ q, level, page: 1, per_page: 200 })}`, { signal }, getAccessToken, '학교 목록을 불러오지 못했습니다.');
  return listOf(d).map((x) => ({
    school_id: x.school_id ?? x.id ?? null, name: x.name ?? '', school_level: x.school_level ?? null,
    region: x.region ?? '', member_count: Number.isInteger(x.member_count) ? x.member_count : null,
  }));
}
/** A1 묶음별 코드 — withdraw_reason · pause_reason · note_category · class_leave_reason · attendance_status 등 */
export async function listCodes(group, { signal = null } = {}, getAccessToken) {
  const d = await call(`/codes${qs({ group })}`, { signal }, getAccessToken, '코드 목록을 불러오지 못했습니다.');
  return listOf(d).filter((c) => !c.archived && !c.archived_at).map((c) => ({ code: c.code, label: c.label ?? c.code, system_kind: c.system_kind ?? null }));
}

// ── 기준 정보 설정(A1~A3 · B2~B6) — 매니저 전용 ──

/** A1 코드 — 설정 화면은 감춘 값까지(include_archived) 번호와 함께 */
export async function listCodesFull(group, { includeArchived = true, signal = null } = {}, getAccessToken) {
  const d = await call(`/codes${qs({ group, include_archived: includeArchived ? 1 : null })}`, { signal }, getAccessToken, '코드 목록을 불러오지 못했습니다.');
  return listOf(d).map((c) => ({
    id: c.id ?? null, group_key: c.group_key ?? group, code: c.code, label: c.label ?? c.code, sort_order: c.sort_order ?? 0,
    archived: !!(c.archived || c.archived_at), system_kind: c.system_kind ?? null,
  })).sort((a, b) => a.sort_order - b.sort_order);
}
/** A2 운영 값 추가 — 시스템 종류 값은 시드로만(출결 상태는 운영 값도 넷 중 하나의 시스템 종류를 가짐) */
export const createCode = ({ groupKey, code, label, sortOrder, systemKind }, gat) => mutate('/codes', 'POST', {
  group_key: groupKey, ...(code ? { code } : {}), label, ...(sortOrder != null ? { sort_order: sortOrder } : {}), ...(systemKind ? { system_kind: systemKind } : {}),
}, gat, '값을 추가하지 못했습니다.');
/** A3 이름 · 순서 · 감춤(V21 — 감춘 값은 고르기에서 빠지고 지난 기록에는 남음) */
export const updateCode = (id, { label, sortOrder, archived }, gat) => mutate(`/codes/${id}`, 'PATCH', {
  ...(label !== undefined ? { label } : {}), ...(sortOrder !== undefined ? { sort_order: sortOrder } : {}), ...(archived !== undefined ? { archived } : {}),
}, gat, '값을 수정하지 못했습니다.');
/** B3 부서 이름 · 부서 자동 선택 기준 · 순서 · 감춤(지금 학생이 있으면 409 in_use) */
export const updateDepartment = (id, { name, schoolLevels, sortOrder, archived }, gat) => mutate(`/departments/${id}`, 'PATCH', {
  ...(name !== undefined ? { name } : {}), ...(schoolLevels !== undefined ? { school_levels: schoolLevels } : {}),
  ...(sortOrder !== undefined ? { sort_order: sortOrder } : {}), ...(archived !== undefined ? { archived } : {}),
}, gat, '부서를 수정하지 못했습니다.');
/** B5 학교 추가 — 같은 (이름 · 학교급 · 지역)이 있으면 409 duplicate + 기존 번호 */
export const createSchool = ({ name, schoolLevel, region }, gat) => mutate('/schools', 'POST', { name, school_level: schoolLevel, region: region || null }, gat, '학교를 추가하지 못했습니다.');
/** B6 학교 수정 */
export const updateSchool = (id, { name, schoolLevel, region }, gat) => mutate(`/schools/${id}`, 'PATCH', {
  ...(name !== undefined ? { name } : {}), ...(schoolLevel !== undefined ? { school_level: schoolLevel } : {}), ...(region !== undefined ? { region } : {}),
}, gat, '학교를 수정하지 못했습니다.');
// 학교 합치기(B7)는 없다 — 오너 결정 2026-10-12(백엔드 1단계 전달 §2 · §3). 잘못 넣은 학생은 학교 기간 정정으로 옮긴 뒤 학교를 지운다

// ── 지우기(백엔드 1단계 전달 §1 · §4-B) — 본문 없음. 딸린 기록이 있으면 409 in_use(몇 명 · 몇 건인지 서버 문구) ──
/** 사유·분류 값 지우기 — 그 값을 쓴 기록이 하나도 없을 때만. 시스템 종류의 마지막 값은 409 last_of_kind */
export const deleteCode = (id, gat) => mutate(`/codes/${id}`, 'DELETE', undefined, gat, '값을 지우지 못했습니다.');
/** 부서 지우기 — 학생 부서 기록과 반(지난 기록 · 비활성 반 포함)이 하나도 없을 때만 */
export const deleteDepartment = (id, gat) => mutate(`/departments/${id}`, 'DELETE', undefined, gat, '부서를 지우지 못했습니다.');
/** 학교 지우기 — 남아 있는 학생 학교 기록이 없을 때만(지운 표시 기록만 남았으면 서버가 연결을 풀고 지운다) */
export const deleteSchool = (id, gat) => mutate(`/schools/${id}`, 'DELETE', undefined, gat, '학교를 지우지 못했습니다.');

// ── 출결 결과(AT1~AT5) — 결과만 입력(요청서 §7-13 · D20) ──

const normSession = (x) => ({
  date: x.date ?? x.session_on, start_time: x.start_time, end_time: x.end_time ?? null,
  kind: x.kind ?? 'REGULAR', status: x.status ?? 'planned', result_id: x.result_id ?? x.id ?? null,
  taught_by: normPerson(x.taught_by), memo: x.memo ?? '',
  counts: { present: 0, late: 0, early_leave: 0, absent: 0, unrecorded: 0, ...(x.counts || {}) },
  roster: arr(x.roster).map((r) => ({ student_id: r.student_id, student_name: r.student_name ?? '', status_code: r.status_code ?? null, memo: r.memo ?? '' })),
});
/** AT1 회차 목록 — 계획 회차와 입력된 결과를 합침. with_roster 면 회차마다 출결 대상 학생. 최대 92일 */
export async function listClassSessions(classId, { from, to, withRoster = false, signal = null } = {}, getAccessToken) {
  const d = await call(`/classes/${classId}/sessions${qs({ from, to, with_roster: withRoster ? 1 : null })}`, { signal }, getAccessToken, '수업 회차를 불러오지 못했습니다.');
  return listOf(d).map(normSession);
}
/** AT2 정규 회차 결과·출결 한 번에 — 출결을 비운 학생은 미입력으로 남음 */
export const saveSessionResult = (classId, date, startTime, { status, taughtByStaffId, endTime, memo, attendance }, gat) => mutate(
  `/classes/${classId}/sessions/${date}/${encodeURIComponent(startTime)}`, 'PUT', {
    status, taught_by_staff_id: taughtByStaffId ?? null, ...(endTime ? { end_time: endTime } : {}), memo: memo || null,
    attendance: (attendance || []).filter((a) => a.status_code).map((a) => ({ student_id: a.student_id, status_code: a.status_code, memo: a.memo || null })),
  }, gat, '출결을 저장하지 못했습니다.');
/** AT3 보강·추가 회차 결과 */
export const saveExtraSession = (classId, { date, startTime, endTime, kind, taughtByStaffId, memo, attendance }, gat) => mutate(`/classes/${classId}/sessions/extra`, 'POST', {
  date, start_time: startTime, end_time: endTime, kind, taught_by_staff_id: taughtByStaffId, memo: memo || null,
  attendance: (attendance || []).filter((a) => a.status_code).map((a) => ({ student_id: a.student_id, status_code: a.status_code, memo: a.memo || null })),
}, gat, '보강·추가 회차를 저장하지 못했습니다.');
/** AT5 학생 출결 — 반별 집계와 목록 */
export async function getStudentAttendance(studentId, { from, to, signal = null } = {}, getAccessToken) {
  const d = await call(`/students/${studentId}/attendance${qs({ from, to })}`, { signal }, getAccessToken, '출결을 불러오지 못했습니다.');
  return {
    from: d?.from ?? from ?? null,
    to: d?.to ?? to ?? null,
    classes: arr(d?.classes).map((c) => ({
      class_id: c.class_id, class_name: c.class_name ?? '',
      counts: { present: 0, late: 0, early_leave: 0, absent: 0, unrecorded: 0, ...(c.counts || {}) },
      recognized_hours: num(c.recognized_hours) ?? 0,
    })),
    items: arr(d?.items).map((x) => ({
      date: x.date, start_time: x.start_time ?? null, class_id: x.class_id, class_name: x.class_name ?? '',
      status: x.status ?? 'planned', status_code: x.status_code ?? null, memo: x.memo ?? '', paused: !!x.paused,
    })),
  };
}

// ── 오류 문구(설계 §5-2 · §8) ──

const REASON_TEXT = {
  // stale 은 학생 쓰기만이 아니라 반 쓰기에서도 온다 — 대신 문구는 대상을 말하지 않는다(서버 문구가 우선)
  stale: '다른 사람이 먼저 고쳤습니다. 새로 불러온 뒤 다시 해 주세요.',
  overlap: '같은 기간에 이미 같은 기록이 있습니다.',
  outside_tenure: '재원 기간 밖의 날짜입니다.',
  class_not_open: '반을 만든 날보다 앞이거나 비활성 반입니다.',
  // 반 편성만이 아니라 휴원 줄도 온다(백엔드 5단계 전달 §3)
  rows_after_withdraw: '퇴원일 다음 날이나 그 뒤에 시작하는 기록이 있습니다. 그 기록을 먼저 지우거나 고친 뒤 퇴원하세요.',
  past_withdrawal: '이미 지난 퇴원은 취소가 아니라 재등록으로 처리합니다.',
  invalid_state: '지금 상태에서 할 수 없는 작업입니다.',
  // 여러 명 입반의 학생별 사유 — 그날 이미 이 반에 있다(백엔드 4단계 전달 §3)
  already_enrolled: '이미 이 반에 있습니다.',
  reason_required: '정정 사유를 적어 주세요.',
  // 둘 다 서버 문구가 먼저다 — 문구가 여럿이다(본인 메모만 · 오늘 담임인 반에만 반 메모 · 학원 전체 메모는 매니저만 …, 백엔드 8단계 전달 §4-G)
  out_of_scope: '담당 반 학생이 아닙니다.',
  forbidden: '이 작업은 매니저·관리자만 할 수 있습니다.',
  // 서버의 date_out_of_range 는 입력 오타 범위(2000-01-01 ~ 오늘+366일)다 — 대신 문구도 그 뜻으로(서버 문구가 우선)
  date_out_of_range: '입력할 수 있는 범위 밖의 날짜입니다.',
  session_not_in_schedule: '그날 시간표에 없는 회차입니다. 보강·추가 회차로 입력해 주세요.',
  not_enrolled_on_date: '그날 이 반에 편성되지 않은 학생이 있습니다.',
  student_paused: '그날 휴원 중인 학생은 출결 대상이 아닙니다.',
  session_canceled: '휴강한 회차에는 출결을 넣을 수 없습니다.',
  not_teacher: '수업한 강사는 강사 직원이어야 합니다.',
  teacher_not_employed: '그날 재직 중인 강사가 아닙니다.',
  class_closed: '비활성 반입니다. 먼저 다시 활성화해 주세요.',
  // 감추기 · 지우기 · 학교급 수정이 같이 쓴다 — 서버 문구(몇 명 · 몇 건)가 먼저 보이고, 이것은 그 문구가 없을 때만
  in_use: '이 값을 쓰는 기록이 있어 할 수 없습니다.',
  duplicate: '같은 값이 이미 있습니다.',
  last_of_kind: '시스템 종류마다 값 하나는 남아야 합니다.',
  system_kind_required: '출결 상태는 출석 · 지각 · 조퇴 · 결석 중 시스템 종류를 골라야 합니다.',
  member_failures: '입반할 수 없는 학생이 있어 아무도 입반하지 않았습니다.',
};

/** 배포 전 서버인가 — 경로가 없어 봉투 없는 404·405 가 온다(강의 v2 와 같은 판정) */
export const isPredeployError = (err) => err != null && (err.routeMissing === true || (err.envelope === false && (err.httpStatus === 404 || err.httpStatus === 405)));

export const PREDEPLOY_TEXT = '준비 중인 기능입니다.';

/**
 * 서버 한글 문구를 사유 문구보다 먼저 보이는 사유(백엔드 0단계 전달 §4-F) — 서버 문구가 더 정확하다.
 *   in_use · duplicate  몇 명이 쓰는지 · 어느 학교와 겹치는지
 *   date_out_of_range   「날짜는 2000-01-01 부터 ○○ 까지만」 — 허용 범위를 서버만 안다
 *   stale               반 쓰기에서도 온다 — 화면 문구는 대상을 모른다
 *   invalid_state       「비활성 반 — 먼저 다시 활성화해 주세요」 · 「삭제된 학생 계정」처럼 까닭을 서버만 안다(백엔드 3단계 전달 §4-H)
 *   class_not_open      「○○반은 10-12 에 만든 반이라…」 · 「비활성 반입니다」 — 반 이름 · 날짜(백엔드 4단계 전달 §4-E)
 *   outside_tenure      「퇴반일은 퇴원일(…)보다 앞이어야…」 · 「재원 기간 밖으로 나가는 반 편성이 있습니다…」
 *   last_of_kind        어느 종류(출석 · 사유 모름 …)의 마지막 값인지(백엔드 4단계 전달 §4-G)
 *   rows_after_withdraw · past_withdrawal  무엇을 먼저 고칠지 · 재등록과 재원 기간 정정 중 어느 길인지(백엔드 5단계 전달 §4-F)
 *   forbidden · out_of_scope  「본인 메모만 고치거나 지울 수 있습니다.」 · 「오늘 담임인 반에만 반 메모를 씁니다.」처럼 까닭이 여럿(백엔드 8단계 전달 §4-G)
 */
const SERVER_TEXT_FIRST = [
  'in_use', 'duplicate', 'date_out_of_range', 'stale', 'invalid_state', 'class_not_open', 'outside_tenure', 'last_of_kind',
  'rows_after_withdraw', 'past_withdrawal', 'forbidden', 'out_of_scope',
];

/** 오류를 사람의 말로 */
export function describeAcademyError(err) {
  if (!err) return '';
  if (isPredeployError(err)) return PREDEPLOY_TEXT;
  // 서버가 실은 문구만 본다(serverMessage). 직접 만든 오류 객체({ reason, message })는 그 칸이 없어 message 를 본다
  const serverText = err.serverMessage !== undefined ? err.serverMessage : err.message;
  if (SERVER_TEXT_FIRST.includes(err.reason) && /[가-힣]/.test(serverText || '')) {
    return err.reason === 'duplicate' && err.data?.field === 'student_no'
      ? serverText.replace(/씁니다\.$/, '사용중입니다.')
      : serverText;
  }
  if (err.reason && REASON_TEXT[err.reason]) return REASON_TEXT[err.reason];
  if (err.httpStatus === 403) return '볼 수 있는 권한이 없습니다.';
  if (err.httpStatus === 404) return '없거나 지워진 기록입니다.';
  return err.message || '요청을 처리하지 못했습니다.';
}
